import { Intent } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import { FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { AlertConfirm, NotificationToaster } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { useDebounce } from 'hooks/useDebounce';
import { PageRestricted } from 'pages/errors/page-restricted';
import { SupplierModal } from 'pages/fleet/fleet-settings';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { FleetService } from 'services';

export function Supplier(props) {
    const requiredAction = "FMS:Manage";
    const canAccessPage = UserHasAccess(requiredAction);

    const debounceTimeout = 750;
    const requestCount = 1000;
    const pageNumber = 1;
    const defaultSortDirection = "Asc";
    const buttonWidth = "40px";

    const defaultSupplier = {
        id: 0,
        name: "",
        categoryId: 1,
        relatedCosts: []
    };
    
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [tableRows, setTableRows] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [noDataMessage, setNoDataMessage] = useState("There are no suppliers, please click 'Add new supplier' to get started.");
    const tableHeaders = ["Name", "Edit", "Delete"];
    const tableColumnWidths = ["100%", buttonWidth, buttonWidth];
    const currentSearchTerm = useRef();
    const inputRef = useRef(null);

    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [supplierToUpdate, setSupplierToUpdate] = useState(defaultSupplier);
    const [showDeleteSupplierModal, setShowDeleteSupplierModal] = useState(false);
    const [selectedSupplierId, setSelectedSupplierId] = useState(0);

    useEffect(searchSuppliers, [])

    useEffect(() => {
        if (currentSearchTerm.current === debouncedSearchTerm) {
            setIsLoadingData(false);
            return;
        }

        searchSuppliers();
    }, [debouncedSearchTerm, pageNumber]);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function searchSuppliers() {
        if (!canAccessPage) {
            return;
        }

        setIsLoadingData(true);

        FleetService.searchSuppliers(requestCount, pageNumber, searchTerm, null, defaultSortDirection)
            .then((suppliersResponse) => {
                if (suppliersResponse === null) {
                    setTableRows([]);
                } else {
                    mapSuppliersToTableRows(suppliersResponse.data);
                }

                setTimeout(function () {
                    if (inputRef.current !== null && inputRef.current.value !== null && inputRef.current.value !== "") {
                        inputRef.current.focus();
                    }
                });
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not fetch suppliers, please try again.");  
            })
            .finally(() => {
                setIsLoadingData(false);
            });
    }

    function mapSuppliersToTableRows(suppliers) {
        const rows = suppliers.map((supplier) => {
            return [
                supplier.name,
                <div key={`${supplier.id}-edit-supplier`}>
                    <Tooltip content={`Edit Supplier`}>
                        <Button
                            onClick={(e) => onEditSupplier(supplier, e)}
                            icon="edit"
                            iconOnly={true}
                            minimal={true}
                            large={false}
                        />
                    </Tooltip>
                </div>,
                <div key={`${supplier.id}-delete-supplier`}>
                    <Tooltip content={`Delete Supplier`}>
                        <Button
                            onClick={(e) => onDeleteSupplier(supplier, e)}
                            intent="delete"
                            icon="trash"
                            iconOnly={true}
                            minimal={true}
                            large={false}
                        />
                    </Tooltip>
                </div>,
            ];
        });
        
        setTableRows(rows);
    }

    function onAddNewSupplierClick() {
        if (!canAccessPage) {
            return;
        }

        setShowSupplierModal(true);
    }

    function onCloseSupplierModal() {
        setShowSupplierModal(false);
        resetSupplierToUpdate();
    }

    function onSaveSupplier() {
        if (!canAccessPage) {
            return;
        }

        setShowSupplierModal(false);
        resetSupplierToUpdate();
        searchSuppliers();
    }

    function resetSupplierToUpdate() {
        setIsModalUpdate(false);
        setSupplierToUpdate({});
    }

    function onEditSupplier(supplier, e) {
        e.stopPropagation();
        setIsModalUpdate(true);
        setSupplierToUpdate(supplier);
        setShowSupplierModal(true);
    }

    function onDeleteSupplier(supplier, e) {
        e.stopPropagation();
        setSelectedSupplierId(supplier.id);
        setShowDeleteSupplierModal(true);
    }

    function onDeleteSupplierConfirm() {
        if (selectedSupplierId === 0) {
            return;
        }

        setIsLoadingData(true);

        FleetService.deleteSupplier(selectedSupplierId)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Supplier deleted successfully.");
                setShowDeleteSupplierModal(false);
                searchSuppliers();
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not delete asset type subtype. Please try again. ");
            })
            .finally(() => {
                setIsLoadingData(false);
            });
        
        setSelectedSupplierId(0);
    }

    function onDeleteSupplierCancel() {
        setShowDeleteSupplierModal(false);  
        setSelectedSupplierId(0);
    }

    useEffect(() => {
        setNoDataMessage("No results found, please try a different search term.");
    }, [debouncedSearchTerm]);

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>

                    <div className="spacer-bottom">
                        <Button id="new-supplier" intent="primary" text="Add new supplier" onClick={onAddNewSupplierClick}/>
                    </div>

                    <FormTextInput 
                        inputRef={inputRef}
                        placeholder="Search suppliers" 
                        disabled={isLoadingData}
                        onChange={onSearchChange} 
                        value={searchTerm} 
                        large 
                        icon="search"
                        id="asset-type-search-field" 
                    />

                    <ListingTable
                        id="listing-table-suppliers"
                        headers={tableHeaders}
                        data={tableRows}
                        columnWidths={tableColumnWidths}
                        pageable={false}
                        loadingData={isLoadingData}
                        noDataMessage={noDataMessage}
                        sortable={false}
                        totalRecordCount={tableRows.length}
                        shrinkLastColumn={true}
                    />

                    <ShowHide
                        evaluator={showSupplierModal}
                        show={(
                            <SupplierModal
                                showModal={showSupplierModal}
                                modalTitle={isModalUpdate ? "Update Supplier" : "Add New Supplier"}
                                isUpdate={isModalUpdate}
                                loading={false}
                                onSave={onSaveSupplier}
                                onCloseModal={onCloseSupplierModal}
                                supplierDetails={supplierToUpdate}
                            />
                        )}
                    />

                    <AlertConfirm
                        isOpen={showDeleteSupplierModal}
                        title={"Delete Supplier?"}
                        onConfirm={onDeleteSupplierConfirm}
                        onCancel={onDeleteSupplierCancel}
                    >
                        <p>Are you sure you want to delete this supplier?</p>
                    </AlertConfirm>

                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}